import { useEffect, useRef, useState } from "react";
import { IoAdd } from "react-icons/io5";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";

import WOService from "src/Services/API/wo.service";
import UserService from "src/Services/API/user.service";
import { handleError } from "src/Common/function";
import TagInput from "src/Pages/WO/Components/forms/components/TagInput";
import {
  ChevronToggle,
  Dropzone,
  FieldLabel,
  FileTypeIcon,
  GhostButton,
  ModalShell,
  PrimaryButton,
  SelectDropdown,
  ServiceTicketToggle,
  TrashIcon,
  compressIfNeeded,
  formatBytes,
} from "./uploadModalUI";
import { noteFilesUploaded } from "src/Pages/KnowledgeBase/kbSession";

// Technical documents are WoAttachment rows with type === 0. The /attach upload
// flow uses type 1 ("attachments to the work order"); the per-file
// metadata.attachmentType then narrows it to a technical document.
const TECHNICAL_DOCUMENT_TYPE = 0;
const ATTACHMENT_UPLOAD_FLOW = 1;

const DOCUMENT_ACCEPT =
  "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/png,image/jpeg,.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg";
const DOCUMENT_FOOTER = "Supported formats: PDF, DOC, DOCX, XLS, XLSX, PNG, JPG";
const DOCUMENT_TYPE_OPTIONS = ["Manual", "Spec Sheet", "Drawing", "Warranty", "Other"];

let itemIdCounter = 0;
const nextItemId = () => `doc-${Date.now()}-${itemIdCounter++}`;

const buildItem = (file) => ({
  id: nextItemId(),
  file,
  documentName: file.name,
  documentType: "",
  tags: [],
  description: "",
  // Default the service-ticket toggle on at upload time.
  includeInServiceTicket: true,
});

/**
 * "Upload Technical Document" modal. Starts on an empty dropzone; once files are
 * chosen it shows an expandable card per file (Document Name / Document Type /
 * Tag / Description / Include In Service Ticket) plus "Add More" and
 * "Upload All (N)".
 *
 * `onUploaded` lets the parent refresh the WO; the modal closes itself after a
 * successful upload.
 */
const UploadDocumentsModal = ({ shown, order, onClose, onUploaded }) => {
  const currentUser = UserService.getCurrentUser();
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const addMoreRef = useRef(null);

  // Reset whenever the modal (re)opens.
  useEffect(() => {
    if (!shown) return;
    setItems([]);
    setExpandedId(null);
    setUploading(false);
  }, [shown]);

  if (!shown) return null;

  const addFiles = (files) => {
    const built = files.map(buildItem);
    setItems((prev) => {
      const next = [...prev, ...built];
      // Expand the first newly-added card when starting from empty.
      if (!prev.length && built[0]) setExpandedId(built[0].id);
      return next;
    });
  };

  const updateItem = (id, patch) =>
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));

  const removeItem = (id) =>
    setItems((prev) => prev.filter((item) => item.id !== id));

  const handleUpload = async () => {
    if (!items.length || !order?.id) return;
    setUploading(true);
    try {
      const files = [];
      const metadata = [];
      for (const item of items) {
        files.push(await compressIfNeeded(item.file, imageCompression));
        metadata.push({
          attachmentType: TECHNICAL_DOCUMENT_TYPE,
          documentName: item.documentName?.trim() || item.file.name,
          documentType: item.documentType || "",
          tags: item.tags,
          description: item.description?.trim() || "",
          includeInServiceTicket: item.includeInServiceTicket,
        });
      }
      const res = await WOService.uploadFiles(files, order.id, ATTACHMENT_UPLOAD_FLOW, currentUser, metadata);
      if (res.status === 201) {
        toast(`${files.length} document${files.length > 1 ? "s" : ""} uploaded successfully`);
        onUploaded?.();
        noteFilesUploaded();
        onClose?.();
      } else {
        toast("Something went wrong");
      }
    } catch (err) {
      handleError(err);
    } finally {
      setUploading(false);
    }
  };

  // ---- Empty state: dropzone ----
  if (!items.length) {
    return (
      <ModalShell
        title="Upload Technical Document"
        onClose={onClose}
        footer={<GhostButton onClick={onClose}>Cancel</GhostButton>}
      >
        <Dropzone
          accept={DOCUMENT_ACCEPT}
          multiple
          footer={DOCUMENT_FOOTER}
          onFiles={addFiles}
          onReject={(message) => toast(message)}
        />
      </ModalShell>
    );
  }

  // ---- File list with per-file metadata ----
  return (
    <ModalShell
      title="Upload Documents"
      badge={`${items.length} File${items.length > 1 ? "s" : ""}`}
      onClose={onClose}
      footer={
        <>
          <label className="h-[40px] px-1 flex items-center gap-2 rounded-[6px] font-OpenSansSemiBold text-[16px] leading-[24px] capitalize text-[#0065FD] cursor-pointer mr-auto">
            <IoAdd size={18} />
            Add More
            <input
              ref={addMoreRef}
              type="file"
              accept={DOCUMENT_ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length) addFiles(files);
                if (e.target) e.target.value = null;
              }}
            />
          </label>
          <GhostButton onClick={onClose} disabled={uploading}>
            Cancel
          </GhostButton>
          <PrimaryButton tour="upload-all" onClick={handleUpload} loading={uploading}>
            Upload All ({items.length})
          </PrimaryButton>
        </>
      }
    >
      <div className="p-6 flex flex-col gap-4">
        {items.map((item) => {
          const expanded = item.id === expandedId;
          return (
            <div
              key={item.id}
              className={`rounded-[12px] border bg-white ${
                expanded ? "border-[#C9D7F2]" : "border-[#E5E8F3]"
              }`}
            >
              {/* Card header — keeps its background whether or not the card is
                  expanded. The card itself isn't clipped (no overflow-hidden) so
                  the Tag dropdown can overflow the card; the header rounds its own
                  corners instead. */}
              <div className={`flex items-center gap-3 px-4 py-3 bg-[#F8F9FE] ${expanded ? "rounded-t-[12px]" : "rounded-[12px]"}`}>
                <FileTypeIcon />
                <div className="flex-1 min-w-0 flex flex-col">
                  <span className="font-OpenSansSemiBold text-[14px] leading-[20px] text-[#1A2332] truncate" title={item.file.name}>
                    {item.file.name}
                  </span>
                  <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A]">
                    {formatBytes(item.file.size)}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => removeItem(item.id)}
                  className="p-2 text-[#7C829A] cursor-pointer hover:bg-slate-100 rounded-[6px]"
                  aria-label="Remove file"
                >
                  <TrashIcon />
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedId(expanded ? null : item.id)}
                  className="p-1 cursor-pointer"
                  aria-label={expanded ? "Collapse" : "Expand"}
                >
                  <ChevronToggle expanded={expanded} />
                </button>
              </div>

              {expanded && (
                <div className="px-4 pb-4 flex flex-col gap-4 border-t border-[#E5E8F3] pt-4">
                  <div className="flex flex-col gap-1">
                    <FieldLabel>Document Name</FieldLabel>
                    <input
                      value={item.documentName}
                      onChange={(e) => updateItem(item.id, { documentName: e.target.value })}
                      className="h-[40px] px-3 border border-[#E5E8F3] rounded-[6px] outline-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]"
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Document Type</FieldLabel>
                    <SelectDropdown
                      value={item.documentType}
                      options={DOCUMENT_TYPE_OPTIONS}
                      placeholder="Select document type"
                      onChange={(documentType) => updateItem(item.id, { documentType })}
                    />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Tag</FieldLabel>
                    <TagInput selectedTags={item.tags} onChange={(tags) => updateItem(item.id, { tags })} />
                  </div>

                  <div className="flex flex-col gap-1">
                    <FieldLabel>Description</FieldLabel>
                    <textarea
                      value={item.description}
                      onChange={(e) => updateItem(item.id, { description: e.target.value })}
                      placeholder="Add notes..."
                      rows={2}
                      className="p-3 bg-[#FBFCFF] border border-[#E5E8F3] rounded-[6px] outline-none resize-none font-OpenSansRegular text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332] placeholder:text-[#868DA6]"
                    />
                  </div>

                  <ServiceTicketToggle
                    checked={item.includeInServiceTicket}
                    onChange={() => updateItem(item.id, { includeInServiceTicket: !item.includeInServiceTicket })}
                    label="Include In Service Ticket"
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ModalShell>
  );
};

export default UploadDocumentsModal;
